import React from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { Screen } from '@/components/ui/Screen';
import { FANTASY } from '@/config';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { DEFAULT_SCORING_RULES } from '@scoring/rules.ts';

const PREDICTION_POINTS = 3;

function PointsRow({ label, points, last }: { label: string; points: number; last?: boolean }) {
  const { arabicNumerals } = useLocale();
  const display = `${points > 0 ? '+' : ''}${points}`;
  return (
    <View
      className={`flex-row items-center justify-between px-4 py-2.5 ${last ? '' : 'border-b border-line'}`}
    >
      <AppText variant="caption" className="flex-1 text-ink">
        {label}
      </AppText>
      <AppText
        variant="caption"
        weight="bold"
        tabular
        className={points >= 0 ? 'text-volt-bright' : 'text-loss'}
      >
        {localizeDigits(display, arabicNumerals)}
      </AppText>
    </View>
  );
}

export default function HowToPlayScreen() {
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const rules = DEFAULT_SCORING_RULES;
  const digits = (value: number) => localizeDigits(String(value), arabicNumerals);

  const rows: { label: string; points: number }[] = [
    { label: t('howTo.rows.played60'), points: rules.minutes.played60 },
    { label: t('howTo.rows.played'), points: rules.minutes.played },
    { label: t('howTo.rows.goalGK'), points: rules.goal.DEF },
    { label: t('howTo.rows.goalMID'), points: rules.goal.MID },
    { label: t('howTo.rows.goalFWD'), points: rules.goal.FWD },
    { label: t('howTo.rows.assist'), points: rules.assist },
    { label: t('howTo.rows.cleanSheetDef'), points: rules.cleanSheet.DEF },
    { label: t('howTo.rows.cleanSheetMid'), points: rules.cleanSheet.MID },
    { label: t('howTo.rows.saves', { count: rules.savesPerPoint }), points: 1 },
    { label: t('howTo.rows.penSave'), points: rules.penaltySaved },
    { label: t('howTo.rows.penMiss'), points: rules.penaltyMissed },
    {
      label: t('howTo.rows.conceded', { count: rules.goalsConcededPer }),
      points: rules.goalsConcededPoints,
    },
    { label: t('howTo.rows.yellow'), points: rules.yellow },
    { label: t('howTo.rows.red'), points: rules.red },
    { label: t('howTo.rows.ownGoal'), points: rules.ownGoal },
    { label: t('howTo.rows.bonus'), points: rules.bonus[0] },
  ];

  return (
    <Screen>
      <BackHeader title={t('howTo.title')} />
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 48, gap: 14 }}>
        <Card className="gap-2 px-4 py-4">
          <AppText variant="heading" className="text-volt-bright">
            {t('howTo.fantasyTitle')}
          </AppText>
          <AppText variant="caption" className="leading-6 text-soft">
            {t('howTo.fantasyIntro', {
              budget: digits(FANTASY.budget),
              multiplier: digits(rules.captainMultiplier),
            })}
          </AppText>
        </Card>

        <View className="gap-2">
          <AppText variant="heading" className="px-1">
            {t('howTo.pointsTitle')}
          </AppText>
          <Card className="overflow-hidden">
            {rows.map((row, index) => (
              <PointsRow
                key={row.label}
                label={row.label}
                points={row.points}
                last={index === rows.length - 1}
              />
            ))}
          </Card>
        </View>

        <Card className="gap-2 px-4 py-4">
          <AppText variant="heading" className="text-volt-bright">
            {t('howTo.predictionsTitle')}
          </AppText>
          <AppText variant="caption" className="leading-6 text-soft">
            {t('howTo.predictionsBody', { points: digits(PREDICTION_POINTS) })}
          </AppText>
        </Card>
      </ScrollView>
    </Screen>
  );
}
